/**
 * @fileoverview Renders a single AI chat message bubble.
 * @module @firesquirrel/ai-chatbot
 *
 * Responsibilities:
 * - Render one user or assistant message with role-appropriate avatar,
 *   alignment, and styling.
 * - Render optional action buttons and token-usage metadata on assistant
 *   messages.
 *
 * Dependencies:
 * - React (peer), lucide-react icons.
 * - `AIChatMessage` type from `../types`.
 * - `AIActionButtons` for action rendering.
 *
 * Architecture:
 * - Purely presentational — receives a message object via props; no state,
 *   no context, no network access.
 *
 * Notes:
 * - Timestamp is rendered via `toLocaleTimeString` at render time.
 */

import { User } from 'lucide-react';
import type { AIChatMessage as MessageType } from '../types';
import AIActionButtons from './AIActionButtons';
import squirrelIconSmall from '../assets/squirrel_icon_small.png';

/**
 * @contract AIChatMessageProps
 * Purpose: props for `<AIChatMessage>`.
 * Fields:  message — the chat message to render (`AIChatMessage` from
 *          `../types`: role, content, timestamp, optional actions,
 *          tokensUsed).
 * Rules:   `message.role` drives alignment/avatar; `actions` and
 *          `tokensUsed` render only on assistant (`role !== 'user'`)
 *          messages.
 * Sync:    none — presentational props only.
 */
interface AIChatMessageProps {
  message: MessageType;
}

/**
 * @component AIChatMessage
 * Purpose:     Renders one chat message bubble (user right-aligned with a
 *              user icon; assistant left-aligned with the squirrel icon).
 * Props:       message: AIChatMessage — see `@contract AIChatMessageProps`.
 * Behavior:    Pure render of props; assistant messages additionally render
 *              `<AIActionButtons>` and token usage when present.
 * Data Source: none (props only — caller reads from `useAIChat()` session
 *              state; this component never fetches).
 * Constraints: no direct API calls; no fetch; no context access.
 * Performance: trivial — re-renders with parent list updates.
 */
export default function AIChatMessage({ message }: AIChatMessageProps) {
  const isUser = message.role === 'user';

  return (
    <div className={`flex gap-3 ${isUser ? 'flex-row-reverse justify-end' : ''}`}>
      {/* Avatar */}
      <div
        className={`shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
          isUser ? 'bg-linear-to-br from-orange-400 to-orange-600' : 'bg-linear-to-br from-orange-500 to-red-600'
        }`}
      >
        {isUser ? (
          <User className="w-4 h-4 text-white" />
        ) : (
          <img src={squirrelIconSmall} alt="AI" className="w-5 h-5 object-contain" width="20" height="20" />
        )}
      </div>

      {/* Message Content */}
      <div className={`flex-1 ${isUser ? 'text-right' : ''}`}>
        <div
          className={`inline-block rounded-lg px-4 py-2 max-w-[85%] ${
            isUser
              ? 'bg-linear-to-r from-orange-500 to-orange-600 text-white'
              : 'bg-gray-100 text-gray-900'
          }`}
        >
          <p className="text-sm whitespace-pre-wrap wrap-break-word">{message.content}</p>
        </div>

        {/* Timestamp */}
        <p className="text-xs text-gray-500 mt-1 px-1">
          {message.timestamp.toLocaleTimeString([], {
            hour: '2-digit',
            minute: '2-digit',
          })}
        </p>

        {/* Action Buttons */}
        {!isUser && message.actions && message.actions.length > 0 && (
          <div className="mt-2">
            <AIActionButtons actions={message.actions} />
          </div>
        )}

        {/* Token Usage (for debugging) */}
        {!isUser && message.tokensUsed && (
          <p className="text-xs text-gray-400 mt-1 px-1">
            Tokens: {message.tokensUsed.input} in, {message.tokensUsed.output} out
          </p>
        )}
      </div>
    </div>
  );
}
